import appStore from '../utils/state';
import gsap from 'gsap';

export default class PasswordModal extends HTMLElement {
  private showTl: gsap.core.Timeline | null = null;
  private hideTl: gsap.core.Timeline | null = null;

  private dialog: HTMLDialogElement | null = null;
  private content: HTMLElement | null = null;
  private closeButton: HTMLButtonElement | null = null;

  private triggerElement: HTMLElement | null = null;

  private unsubscribeFromPasswordModalOpenState: (() => void) | null = null;

  private onPasswordModalToggleBound = this.onPasswordModalToggle.bind(this);
  private onCloseButtonClickBound = this.onCloseButtonClick.bind(this);
  private onDialogCancelBound = this.onDialogCancel.bind(this);
  private onDialogCloseBound = this.onDialogClose.bind(this);
  private onDialogClickBound = this.onDialogClick.bind(this);

  connectedCallback(): void {
    this.dialog = this.querySelector('dialog');
    this.content = this.querySelector('[data-password-modal-ref="content"]');
    this.closeButton = this.querySelector('[data-password-modal-ref="close-button"]');

    this.addEventListeners();
    this.subscribe();

    if (this.dataset.openOnLoad === 'true' || this.querySelector('[data-password-modal-ref="has-errors"]')) {
      appStore.actions.openPasswordModal();
    }
  }

  addEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.addEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.addEventListener('cancel', this.onDialogCancelBound);
      this.dialog.addEventListener('close', this.onDialogCloseBound);
      this.dialog.addEventListener('click', this.onDialogClickBound);
    }
  }

  removeEventListeners(): void {
    if (this.closeButton) {
      this.closeButton.removeEventListener('click', this.onCloseButtonClickBound);
    }
    if (this.dialog) {
      this.dialog.removeEventListener('cancel', this.onDialogCancelBound);
      this.dialog.removeEventListener('close', this.onDialogCloseBound);
      this.dialog.removeEventListener('click', this.onDialogClickBound);
    }
  }

  subscribe(): void {
    this.unsubscribeFromPasswordModalOpenState = appStore.subscribeKey(
      'isPasswordModalOpen',
      this.onPasswordModalToggleBound,
    );
  }

  unsubscribe(): void {
    if (this.unsubscribeFromPasswordModalOpenState) {
      this.unsubscribeFromPasswordModalOpenState();
    }
  }

  onCloseButtonClick(): void {
    appStore.actions.closePasswordModal();
  }

  onDialogCancel(event: Event): void {
    event.preventDefault();
    appStore.actions.closePasswordModal();
  }

  onDialogClick(event: MouseEvent): void {
    if (event.target === this.dialog) {
      appStore.actions.closePasswordModal();
    }
  }

  onDialogClose(): void {
    if (appStore.getState().isPasswordModalOpen) {
      appStore.actions.closePasswordModal();
    }

    this.restoreFocus();
  }

  captureTrigger(): void {
    const trigger = document.querySelector('[aria-controls="password-modal-dialog"]');

    if (trigger instanceof HTMLElement) {
      this.triggerElement = trigger;
      return;
    }

    const activeElement = document.activeElement;

    if (activeElement instanceof HTMLElement && !this.contains(activeElement)) {
      this.triggerElement = activeElement;
    }
  }

  restoreFocus(): void {
    if (this.triggerElement && document.contains(this.triggerElement)) {
      this.triggerElement.focus();
    }

    this.triggerElement = null;
  }

  onPasswordModalToggle(isPasswordModalOpen: boolean): void {
    if (isPasswordModalOpen) {
      this.show();
    } else {
      this.hide();
    }
  }

  show(): void {
    if (!this.dialog) {
      return;
    }

    if (this.hideTl) {
      this.hideTl.kill();
    }
    if (this.showTl) {
      this.showTl.kill();
    }

    gsap.set(this.dialog, { autoAlpha: 0, visibility: 'visible' });

    this.captureTrigger();

    if (!this.dialog.open) {
      this.dialog.showModal();
    }

    this.showTl = gsap.timeline();
    this.showTl.fromTo(this.dialog, { autoAlpha: 0 }, { autoAlpha: 1, duration: 0.3, ease: 'power2.out' }, 0);
    this.showTl.fromTo(this.dialog, { y: 20 }, { y: 0, duration: 0.7, ease: 'power3.out' }, 0);
  }

  hide(): void {
    if (!this.dialog) {
      return;
    }

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }

    this.hideTl = gsap.timeline();

    this.hideTl.to(this.dialog, { autoAlpha: 0, duration: 0.2, ease: 'power2.out' }, 0);
    if (this.dialog.open) {
      this.dialog.close();
    }
  }

  disconnectedCallback(): void {
    this.removeEventListeners();
    this.unsubscribe();

    if (this.showTl) {
      this.showTl.kill();
    }
    if (this.hideTl) {
      this.hideTl.kill();
    }
  }
}

customElements.define('password-modal', PasswordModal);
